/**
 * WindowResults.js
 * Implements the results window
 */

import {
    Game,
    GameWindow,
    OptionsMenuMode,
    UiElement,
    UiButton,
    WindowLayers,
    UiImage,
    GameSpine,
    GameTween,
    UiText,
    UiFrame,
    GameTimer
} from "pixi_core";

import {
    GameEvents,
    MUSIC_FADE_OUT_TIME,
    MUSIC_FADE_IN_TIME,
    GEAR_MENU_Y_POS,
    RESULT_VICTORY_ANIMATION_DELAY,
    RESULT_VICTORY_ANIMATION_TIME,
    RESULT_VICTORY_ANIMATION_START_SCALE,
    GEAR_MENU_X_POS,
    SETTINGS_PREFAB,
    OPTIONS_PREFAB,
    Cinematic
} from "../Constants";

export class WindowResults extends GameWindow {
    constructor() {
        super({
            name: "WindowResults",
            isResultsWindow: true,
            layer: WindowLayers.eUNDER_GEAR,
            disableInputUnder: true,
            showDarkenUnder: true,
            ignoreGearButton: true,
            ignoreAndroidBackButton: true,
            ignoreOutsideZoneTap: true
        });

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {UiButton} */
        this._btnLevelSelect = null;

        /** @private @type {UiButton} */
        this._btnNext = null;

        /** @private @type {UiButton} */
        this._btnRestart = null;

        /** @private @type {UiImage} */
        this._backgroundVictory = null;

        /** @private @type {UiImage} */
        this._backgroundDefeat = null;

        /** @private @type {UiFrame} */
        this._frameAnimation = null;

        /** @private @type {UiText} */
        this._messageVictory = null;

        /** @private @type {Boolean} */
        this._winGame = false;

        /** @private @type {Boolean} */
        this._isLastLevel = false;

        /** @private @type {GameSpine} */
        this._spineVictoryAnimation = null;

        /** @private @type {GameTimer} */
        this._timerVictoryDelay = null;

        /** @private @type {GameTween} */
        this._tweenVictoryMessage = null;

        Game.on(GameEvents.eRESULTS_NEXT, this._onBtnNext, this);
        Game.on(GameEvents.eRESULTS_LEVELS, this._onBtnLevelSelection, this);
        Game.on(GameEvents.eRESULTS_RESTART, this._onBtnRestart, this);
        Game.on(GameEvents.eGAME_WIN_LEVEL, this._onWin, this);
        Game.on(GameEvents.eGAME_LOSE_LEVEL, this._onLose, this);
    }

    /**
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("Results", this);
        this._btnLevelSelect = this._ui.findElementByName("BtnLevelSelection");
        this._btnNext = this._ui.findElementByName("BtnNext");
        this._btnRestart = this._ui.findElementByName("BtnRestart");
        this._backgroundVictory = this._ui.findElementByName("BackgroundVictory");
        this._backgroundDefeat = this._ui.findElementByName("BackgroundDefeat");
        this._frameAnimation = this._ui.findElementByName("FrameAnimation");
        this._messageVictory = this._backgroundVictory.findElementByName("LblTitle");

        this.resetNavigationButtonGroups();
        this.setDefaultNavigationButton(this._btnNext);
    }

    /**
     * @protected
     */
    _onWindowOpening() {
        Game.pause("Results");

        // Configure options menu for this context
        Game.windows.optionsMenu.reset({
            mode: OptionsMenuMode.eOPTIONS,
            prefab: OPTIONS_PREFAB,
            prefabSettings: SETTINGS_PREFAB,
            exclude: ["BtnHome", "BtnRestart", "BtnLevelSelection"]
        });

        // Configure gear button for this context
        Game.windows.showGearButton({
            fullScreen: true,
            idSprite: "BTN_Gear",
            idSpriteOver: "BTN_GearHover",
            idSpritePressed: "BTN_GearPressed",
            idSpriteBack: "BTN_Back",
            idSpriteBackOver: "BTN_BackHover",
            idSpriteBackPressed: "BTN_BackPressed",
            scalePressed: 1,
            x: GEAR_MENU_X_POS,
            y: GEAR_MENU_Y_POS
        });

        this._btnNext.visible = this._winGame;
        this._btnRestart.visible = !this._winGame;
        this._btnLevelSelect.visible = !this._winGame || (this._winGame && !this._isLastLevel);
        this._backgroundVictory.setVisible(this._winGame);
        this._backgroundDefeat.setVisible(!this._winGame);

        if (this._winGame) {
            Game.audio.playSfx("IDSND_RES_VICTORY_MSG");
            Game.audio.playMusic("IDSND_MUSIC_VICTORY", { fadeOutTime: MUSIC_FADE_OUT_TIME, fadeInTime: MUSIC_FADE_IN_TIME });
            this._playVictoryMessage();
        } else {
            Game.audio.stopCurrentMusic();
            Game.audio.playSfx("IDSND_RES_DEFEAT_MSG", {
                ignoreNonSystemPauseEvents: true
            });
        }

        if (this._winGame) {
            this.setCurrentNavigationButton(this._btnNext);
        } else {
            this.setCurrentNavigationButton(this._btnRestart);
        }
    }

    /**
     * @protected
     */
    _onWindowClosed() {
        Game.resume("Results");
        this._clearTransitions();
    }

    /**
     * @private
     */
    _onBtnNext() {
        Game.audio.playSfx("IDSND_NAVIG_FORWARD");

        if (this._isLastLevel) {
            Game.fire(GameEvents.eCINEMATIC_SET, Cinematic.eENDING);
            Game.windows.changeContext("WindowCinematics");
        } else {
            Game.fire(GameEvents.eGAME_NEXT_LEVEL);
            Game.windows.changeContext("WindowGame");
        }
    }

    /**
     * @private
     */
    _onBtnLevelSelection() {
        Game.audio.playSfx("IDSND_NAVIG_FORWARD");
        Game.windows.changeContext("WindowLevelSelection");
    }

    /**
     * @private
     */
    _onBtnRestart() {
        Game.audio.playSfx("IDSND_NAVIG_FORWARD");
        Game.windows.changeContext("WindowGame");
    }

    /**
     * @private
     * @param {Boolean} isLastLevel
     */
    _onWin(isLastLevel) {
        this._isLastLevel = isLastLevel;
        this._winGame = true;
    }

    /**
     * @private
     */
    _onLose() {
        this._winGame = false;
    }

    /**
     * @private
     */
    _playVictoryMessage() {
        this._clearTransitions();

        this._spineVictoryAnimation = new GameSpine("Victory");
        this._frameAnimation.uiRoot.addChild(this._spineVictoryAnimation);
        this._spineVictoryAnimation.setAnimation("animation", false);

        this._messageVictory.scale = RESULT_VICTORY_ANIMATION_START_SCALE;

        this._tweenVictoryMessage = this.createTween(this._messageVictory)
            .to({ scale: 1 }, RESULT_VICTORY_ANIMATION_TIME)
            .easing(GameTween.Easing.ElasticOut);
        this._timerVictoryDelay = Game.windows.getCurrentWindow().createTimer(RESULT_VICTORY_ANIMATION_DELAY);
        this._timerVictoryDelay.onComplete = () => {
            this._tweenVictoryMessage.start();
        };
    }

    /**
     * @private
     */
    _clearTransitions() {
        if (this._spineVictoryAnimation) {
            this._frameAnimation.uiRoot.removeChild(this._spineVictoryAnimation);
            this._spineVictoryAnimation.destroy();
            this._spineVictoryAnimation = null;
        }

        if (this._timerVictoryDelay) {
            this._timerVictoryDelay.destroy();
            this._timerVictoryDelay = null;
        }
        if (this._tweenVictoryMessage) {
            this._tweenVictoryMessage.destroy();
            this._tweenVictoryMessage = null;
        }
    }
}
